import { communicationService } from '@ad-engine/communication';
import { context, utils, type AdSlot } from '@ad-engine/core';
import { AdvertisementLabel, AdvertiserInfoDialog } from '../../../ad-products';
import { DSA_AVAILABLE } from '../../../communication/events/events-bidders';
import { DsaData } from '../utils/dsa';

type DsaRenderProps = DsaData;

export class DsaHandler {
	private isRegistered = false;

	isEnabled(): boolean {
		return context.get('options.dsa.modal.enabled') ?? false;
	}

	register(): void {
		if (this.isRegistered) {
			utils.logger('DSA', 'DSA handler already registered');
			return;
		}

		this.isRegistered = true;

		utils.logger('DSA', 'Registering DSA handler');

		communicationService.on(
			DSA_AVAILABLE,
			({ slot, behalf, paid }: DsaData) => {
				utils.logger('DSA', 'DSA data available for slot ' + slot.getSlotName(), {
					slot,
					behalf,
					paid,
				});

				if (!slot) {
					utils.logger('DSA', 'Slot does not exist');
					return;
				}

				if (!slot.getElement()) {
					utils.logger('DSA', 'Slot ' + slot.getSlotName() + ' element does not exist');
					return;
				}

				this.render({ slot, behalf, paid });
			},
			false,
		);
	}

	render({ behalf, paid, slot }: DsaRenderProps): void {
		if (!this.isEnabled()) {
			return;
		}

		// For interstitial, do not render the dialog
		if (slot.getSlotName() === 'interstitial') {
			return;
		}

		const advertiserInfoDialog = new AdvertiserInfoDialog({
			behalf,
			paid,
			slot: slot.getSlotName(),
		});

		const dialogElement = advertiserInfoDialog.getElement();
		if (this.alreadyHasDsaModal(dialogElement.id)) {
			return;
		}

		document.body.appendChild(advertiserInfoDialog.render());
		this.renderAdLabel(slot);
	}

	rerender(slot: AdSlot) {
		if (!this.isEnabled()) {
			return;
		}

		const dialogId = AdvertiserInfoDialog.getDialogId(slot.getSlotName());
		const dialog = document.getElementById(dialogId);

		if (dialog === null) {
			return;
		}

		dialog.remove();
		this.renderAdLabel(slot);
	}

	private renderAdLabel(slot: AdSlot): void {
		const adSlotElement = slot.getElement();
		if (!adSlotElement) {
			return;
		}

		AdvertisementLabel.clearPlaceholderLabels(slot);
		utils.logger('DSA', 'Rendering ad label for slot ' + slot.getSlotName());
		const adLabel = adSlotElement.querySelector(`.${utils.AD_LABEL_CLASS}`);
		if (adLabel) {
			adLabel.remove();
		}

		const newAdLabel = new AdvertisementLabel({
			slot: slot.getSlotName(),
		});
		adSlotElement.appendChild(newAdLabel.render());
	}

	private alreadyHasDsaModal(dialogId: string): boolean {
		const dialogElement = document.getElementById(dialogId);
		return dialogElement !== null && dialogElement.nodeName === 'DIALOG';
	}

	reset() {
		this.isRegistered = false;
	}
}

export const dsaHandler = new DsaHandler();
